import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, TrendingDown, TrendingUp, Sparkles } from "lucide-react";
import { CardWatchLogo } from "@/components/CardWatchLogo";
import BottomNav from "@/components/BottomNav";
import SettingsDialog from "@/components/SettingsDialog";
import UpgradeDialog from "@/components/UpgradeDialog";
import ProLock from "@/components/ProLock";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencyFormat = (value, currency = "GBP") => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const Simulator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [newBalance, setNewBalance] = useState(0);
  const [newLimit, setNewLimit] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isPro = data?.plan?.name === "pro";

  useEffect(() => {
    const load = async () => {
      if (!user?.user_id) return;
      try {
        const res = await axios.get(`${API}/saltedge/dashboard`, {
          params: { user_id: user.user_id },
          withCredentials: true,
        });
        setData(res.data);
        const perCard = res.data?.utilization?.per_card || [];
        if (perCard.length > 0) {
          const first = perCard[0];
          setSelectedId(first.card_id);
          setNewBalance(Number(first.balance || 0));
          setNewLimit(Number(first.credit_limit || 0));
        }
      } catch (e) {
        toast.error("Could not load your cards");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const cards = data?.utilization?.per_card || [];
  const currency = data?.currency || "GBP";
  const selected = cards.find((c) => c.card_id === selectedId);

  const scenario = useMemo(() => {
    if (!selected || !cards.length) return null;
    const balance = clamp(Number(newBalance) || 0, 0, 999999);
    const limit = clamp(Number(newLimit) || 1, 1, 999999);
    const cardUtilNext = (balance / limit) * 100;
    const cardUtilNow = selected.credit_limit
      ? (Number(selected.balance) / Number(selected.credit_limit)) * 100
      : 0;

    // Recompute overall utilization if we swap this card
    let totalLimitNext = 0;
    let totalBalanceNext = 0;
    let totalLimitNow = 0;
    let totalBalanceNow = 0;
    cards.forEach((c) => {
      const b = Number(c.balance || 0);
      const l = Number(c.credit_limit || 0);
      totalLimitNow += l;
      totalBalanceNow += b;
      if (c.card_id === selectedId) {
        totalLimitNext += limit;
        totalBalanceNext += balance;
      } else {
        totalLimitNext += l;
        totalBalanceNext += b;
      }
    });
    const overallNow = totalLimitNow ? (totalBalanceNow / totalLimitNow) * 100 : 0;
    const overallNext = totalLimitNext ? (totalBalanceNext / totalLimitNext) * 100 : 0;

    return {
      cardUtilNow,
      cardUtilNext,
      overallNow,
      overallNext,
      delta: overallNext - overallNow,
      balanceDelta: balance - Number(selected.balance || 0),
    };
  }, [cards, selected, selectedId, newBalance, newLimit]);

  const pickCard = (c) => {
    setSelectedId(c.card_id);
    setNewBalance(Number(c.balance || 0));
    setNewLimit(Number(c.credit_limit || 0));
  };

  return (
    <div className="min-h-screen bg-[#060B19] text-white">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/30 blur-[160px]" />

      <header className="relative z-10 bg-[#060B19]/70 backdrop-blur-xl border-b border-white/5">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="simulator-logo-link">
              <CardWatchLogo size={34} />
            </Link>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="simulator-back-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-white/30 px-3 sm:px-4 py-2 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={13} /> <span className="hidden xs:inline">Back to</span> Dashboard
          </button>
        </div>
      </header>

      <main className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-14 pb-28 md:pb-14">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[#00E676] font-bold">
            Simulator
          </div>
          <h1
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="simulator-title"
          >
            What if you paid it down?
          </h1>
          <p className="mt-2 text-sm text-slate-300">
            Adjust a card's balance or credit limit and see how your utilization shifts —
            no changes are saved.
          </p>
        </div>

        <ProLock
          locked={!loading && !isPro}
          title="Credit Simulator"
          description="Model what-if scenarios on any card before you actually make a payment."
          bullets={[
            "Adjust balance or credit-limit and see the delta live",
            "Overall utilization impact across every linked card",
            "Instant feedback — nothing is ever saved",
          ]}
          onUpgrade={() => setUpgradeOpen(true)}
          testid="simulator-pro-lock"
        >
          {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-sm text-slate-300" data-testid="simulator-loading">
            Loading your cards…
          </div>
        ) : cards.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center" data-testid="simulator-empty">
            <p className="text-sm text-slate-300 mb-4">Add a card first to try scenarios.</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-5 py-2.5 text-xs"
            >
              Back to dashboard
            </button>
          </div>
        ) : (
          <>
            {/* Card picker */}
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold mb-3">
                Pick a card
              </div>
              <div className="flex flex-wrap gap-2" data-testid="simulator-card-picker">
                {cards.map((c) => {
                  const active = c.card_id === selectedId;
                  return (
                    <button
                      key={c.card_id}
                      onClick={() => pickCard(c)}
                      data-testid={`simulator-pick-${c.card_id}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors border ${
                        active
                          ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                          : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30"
                      }`}
                    >
                      {c.nickname || c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sliders */}
            {selected && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-7 mb-6"
                data-testid="simulator-controls"
              >
                <div className="mb-6">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label
                      htmlFor="sim-balance-input"
                      className="text-xs font-semibold text-slate-300"
                    >
                      New balance
                    </label>
                    <div
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#060B19] px-2 py-1 focus-within:border-[#00E676] focus-within:ring-2 focus-within:ring-[#00E676]/20 transition-colors"
                      data-testid="simulator-balance-value"
                    >
                      <span
                        className="text-xs text-slate-400 tabular-nums select-none"
                        aria-hidden="true"
                      >
                        {currency === "GBP"
                          ? "£"
                          : currency === "USD"
                          ? "$"
                          : currency === "EUR"
                          ? "€"
                          : currency}
                      </span>
                      <input
                        id="sim-balance-input"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={999999}
                        step={1}
                        value={newBalance}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 0 : Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          setNewBalance(clamp(v, 0, 999999));
                        }}
                        onBlur={(e) =>
                          setNewBalance(clamp(Number(e.target.value) || 0, 0, 999999))
                        }
                        data-testid="simulator-balance-input"
                        className="w-24 sm:w-28 bg-transparent text-right text-sm font-semibold text-white outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={Math.max(1000, Math.round(Number(selected.credit_limit || 1000) * 1.2))}
                    step={10}
                    value={newBalance}
                    onChange={(e) => setNewBalance(Number(e.target.value))}
                    data-testid="simulator-balance-slider"
                    className="w-full accent-[#00E676] cursor-pointer"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-slate-300">
                    <span>{currencyFormat(0, selected.currency || currency)}</span>
                    <span>
                      Current:{" "}
                      {currencyFormat(selected.balance, selected.currency || currency)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label
                      htmlFor="sim-limit-input"
                      className="text-xs font-semibold text-slate-300"
                    >
                      New credit limit
                    </label>
                    <div
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#060B19] px-2 py-1 focus-within:border-[#00E676] focus-within:ring-2 focus-within:ring-[#00E676]/20 transition-colors"
                      data-testid="simulator-limit-value"
                    >
                      <span
                        className="text-xs text-slate-400 tabular-nums select-none"
                        aria-hidden="true"
                      >
                        {currency === "GBP"
                          ? "£"
                          : currency === "USD"
                          ? "$"
                          : currency === "EUR"
                          ? "€"
                          : currency}
                      </span>
                      <input
                        id="sim-limit-input"
                        type="number"
                        inputMode="decimal"
                        min={1}
                        max={999999}
                        step={1}
                        value={newLimit}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 1 : Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          setNewLimit(clamp(v, 1, 999999));
                        }}
                        onBlur={(e) =>
                          setNewLimit(clamp(Number(e.target.value) || 1, 1, 999999))
                        }
                        data-testid="simulator-limit-input"
                        className="w-24 sm:w-28 bg-transparent text-right text-sm font-semibold text-white outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    min={100}
                    max={Math.max(50000, Math.round(Number(selected.credit_limit || 100) * 3))}
                    step={100}
                    value={newLimit}
                    onChange={(e) => setNewLimit(Number(e.target.value))}
                    data-testid="simulator-limit-slider"
                    className="w-full accent-[#00E676] cursor-pointer"
                  />
                  <div className="flex justify-between mt-1 text-[10px] text-slate-300">
                    <span>{currencyFormat(100, selected.currency || currency)}</span>
                    <span>
                      Current:{" "}
                      {currencyFormat(selected.credit_limit, selected.currency || currency)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Impact panels */}
            {scenario && selected && (
              <div className="grid gap-4 sm:grid-cols-2">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.05 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
                  data-testid="simulator-card-impact"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-bold">
                    {selected.nickname || selected.name} · utilization
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className="text-3xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {scenario.cardUtilNext.toFixed(1)}%
                    </span>
                    <span className="text-xs text-slate-300">
                      was {scenario.cardUtilNow.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full bg-[#00E676] transition-all"
                      style={{ width: `${clamp(scenario.cardUtilNext, 0, 100)}%` }}
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 }}
                  className="rounded-2xl border border-[#00E676]/25 bg-gradient-to-br from-[#00E676]/[0.06] to-transparent p-5"
                  data-testid="simulator-overall-impact"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
                    <Sparkles size={11} /> Overall utilization
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className="text-3xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {scenario.overallNext.toFixed(1)}%
                    </span>
                    <span className="text-xs text-slate-300">
                      was {scenario.overallNow.toFixed(1)}%
                    </span>
                  </div>
                  <div
                    className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold ${
                      scenario.delta < 0
                        ? "text-[#00E676]"
                        : scenario.delta > 0
                        ? "text-red-400"
                        : "text-slate-300"
                    }`}
                  >
                    {scenario.delta < 0 ? (
                      <TrendingDown size={13} />
                    ) : scenario.delta > 0 ? (
                      <TrendingUp size={13} />
                    ) : null}
                    {scenario.delta === 0
                      ? "No change"
                      : `${scenario.delta > 0 ? "+" : ""}${scenario.delta.toFixed(1)} pts`}
                  </div>
                </motion.div>
              </div>
            )}
          </>
        )}
        </ProLock>
      </main>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={data?.settings}
        plan={data?.plan}
        onSaved={() => {}}
      />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <BottomNav onOpenSettings={() => setSettingsOpen(true)} />
    </div>
  );
};

export default Simulator;
